import React from 'react';
import {Box} from '@mui/material';
import WaterDropOutlinedIcon from '@mui/icons-material/WaterDropOutlined';
import BuildIcon from '@mui/icons-material/Build';
import BrushOutlinedIcon from '@mui/icons-material/BrushOutlined';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import Constants from '../../../utils/constants.js';
import {PhotoTile} from '../../../app/components/interventions/v2/primitives.js';
import {ACTE_COULEURS, CARTE, PHOTO_ETIQUETTES, TEXTE, TITRE_SECTION} from '../../tokens.js';
import {formatDate, photosEvenement} from './fiche.selectors.js';

const ASSETS = Constants.API_ASSETS_PHOTO_INTERVENTION;
const ROUGE = ACTE_COULEURS.probleme.encre;

const TOKENS = {
    lavage: {bg: ACTE_COULEURS.lavage.fond, ink: ACTE_COULEURS.lavage.encre, dot: ACTE_COULEURS.lavage.rond},
    maint: {bg: ACTE_COULEURS.maint.fond, ink: ACTE_COULEURS.maint.encre, dot: ACTE_COULEURS.maint.rond},
    graffiti: {bg: ACTE_COULEURS.graffiti.fond, ink: ACTE_COULEURS.graffiti.encre, dot: ACTE_COULEURS.graffiti.encre},
    impossible: {bg: ACTE_COULEURS.probleme.fond, ink: ROUGE, dot: ROUGE},
};

/** Gabarit d'un bloc d'acte (handoff §2) : carte radius 16, en-tête teinté 12x20, corps dessous. */
export function FicheBlocActe({numero, titre, Icone, tokens, valeur, children}) {
    return (
        <Box component="section" aria-label={titre} sx={{bgcolor: CARTE.fond, border: `1px solid ${CARTE.bordure}`, borderRadius: `${CARTE.radius}px`, boxShadow: CARTE.ombre, overflow: 'hidden'}}>
            <Box sx={{display: 'flex', alignItems: 'center', gap: '12px', px: '20px', py: '12px', bgcolor: tokens.bg, color: tokens.ink}}>
                <Box component="span" sx={{width: 26, height: 26, borderRadius: '50%', bgcolor: tokens.dot, color: '#FFFFFF', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 800}}>{numero}</Box>
                <Icone sx={{fontSize: 18}}/>
                <Box component="h2" sx={{m: 0, fontSize: 12, fontWeight: 800, letterSpacing: '.14em', textTransform: 'uppercase'}}>{titre}</Box>
                {valeur && <Box sx={{ml: 'auto', fontSize: 13, fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: '6px'}}>{valeur}</Box>}
            </Box>
            {children && <Box sx={{px: '20px', pt: '16px', pb: '20px'}}>{children}</Box>}
        </Box>
    );
}

const grilleSx = {display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '16px'};

function Tuile({chemins, label, color, onOuvrir}) {
    return (
        <Box sx={{width: '100%', maxWidth: 440, minWidth: 0}}>
            <PhotoTile photos={chemins} label={label} color={color} assetsUrl={ASSETS} maxWidth={440} autoPlay={false} onOpen={onOuvrir}/>
        </Box>
    );
}

/** Encart problème : fond rouge pâle, grille `minmax(300px, 440px) | 1fr` (une seule colonne quand la zone n'a pas de photo). */
function EncartProbleme({zone, photos, couleur, texte, onOuvrir}) {
    return (
        <Box sx={{display: 'grid', gridTemplateColumns: photos.length > 0 ? {xs: '1fr', sm: 'minmax(300px, 440px) minmax(0, 1fr)'} : '1fr', gap: '20px', p: '16px', borderRadius: '12px', bgcolor: ACTE_COULEURS.probleme.fond, border: `1px solid ${ACTE_COULEURS.probleme.bordure}`}}>
            {photos.length > 0 && <Tuile chemins={photos} label={zone.toUpperCase()} color={couleur} onOuvrir={onOuvrir}/>}
            <Box sx={{display: 'flex', flexDirection: 'column', gap: '6px', pt: '4px'}}>
                <Box component="span" sx={{fontSize: 11, fontWeight: 800, letterSpacing: '.14em', textTransform: 'uppercase', color: ROUGE}}>Problème</Box>
                <Box component="span" sx={{fontSize: 20, fontWeight: 800, color: TEXTE}}>{zone}</Box>
                {texte && <Box component="span" sx={{fontSize: 13, color: '#5F6770'}}>{texte}</Box>}
            </Box>
        </Box>
    );
}

function BlocLavage({evenement, numero, quantite, onPhotos}) {
    const photos = evenement.photos || [];
    const toutes = photosEvenement(evenement);
    const ouvrir = (src) => onPhotos(toutes, src);
    const avant = photos.filter((p) => p.etiquette === 'AVANT').map((p) => p.url);
    const apres = photos.filter((p) => p.etiquette === 'APRES').map((p) => p.url);
    const zones = new Map();
    for (const p of photos) {
        if (p.etiquette === 'AVANT' || p.etiquette === 'APRES') continue;
        if (!zones.has(p.etiquette)) zones.set(p.etiquette, []);
        zones.get(p.etiquette).push(p.url);
    }
    const n = evenement.detail ? evenement.detail.nombreLaves : null;
    const valeur = n != null && quantite != null ? `${n} / ${quantite} bacs` : null;
    const aDuContenu = avant.length > 0 || apres.length > 0 || zones.size > 0;
    return (
        <FicheBlocActe numero={numero} titre="Lavage" Icone={WaterDropOutlinedIcon} tokens={TOKENS.lavage} valeur={valeur}>
            {aDuContenu && (
                <Box sx={{display: 'flex', flexDirection: 'column', gap: '14px'}}>
                    {(avant.length > 0 || apres.length > 0) && (
                        <Box sx={grilleSx}>
                            {avant.length > 0 && <Tuile chemins={avant} label="AVANT" color={PHOTO_ETIQUETTES.AVANT} onOuvrir={ouvrir}/>}
                            {apres.length > 0 && <Tuile chemins={apres} label="APRÈS" color={PHOTO_ETIQUETTES.APRES} onOuvrir={ouvrir}/>}
                        </Box>
                    )}
                    {[...zones.entries()].map(([zone, chemins]) => (
                        <EncartProbleme key={zone} zone={zone} photos={chemins} couleur={ACTE_COULEURS.lavage.rond} onOuvrir={ouvrir}/>
                    ))}
                </Box>
            )}
        </FicheBlocActe>
    );
}

function BlocMaintenance({evenement, numero, onPhotos}) {
    const elements = evenement.elements || [];
    const problemes = elements.filter((el) => el.statut === 'PROBLEME');
    const ok = elements.filter((el) => el.statut !== 'PROBLEME');
    const toutes = photosEvenement(evenement);
    const ouvrir = (src) => onPhotos(toutes, src);
    const valeur = problemes.length > 0
        ? (
            <Box component="span" sx={{display: 'inline-flex', alignItems: 'center', gap: '6px', color: ROUGE}}>
                <WarningAmberIcon sx={{fontSize: 16}}/>
                {`${problemes.length} zone${problemes.length > 1 ? 's' : ''} en problème${ok.length > 0 ? ` · ${ok.length} OK` : ''}`}
            </Box>
        )
        : (ok.length > 0 ? `${ok.length} zone${ok.length > 1 ? 's' : ''} OK` : null);
    return (
        <FicheBlocActe numero={numero} titre="Maintenance préventive" Icone={BuildIcon} tokens={TOKENS.maint} valeur={valeur}>
            {elements.length > 0 && (
                <Box sx={{display: 'flex', flexDirection: 'column', gap: '14px'}}>
                    {problemes.map((el) => (
                        <EncartProbleme
                            key={el.libelle}
                            zone={el.libelle}
                            photos={(el.photos || []).map((p) => p.url)}
                            couleur={ACTE_COULEURS.maint.rond}
                            texte={`Constaté lors du passage du ${formatDate(evenement.date)}. Reste en défaut jusqu'à la prochaine inspection.`}
                            onOuvrir={ouvrir}
                        />
                    ))}
                    {ok.length > 0 && (
                        <Box sx={{display: 'flex', flexDirection: 'column', gap: '8px'}}>
                            <Box component="span" sx={{fontSize: 11, fontWeight: 700, letterSpacing: '.12em', textTransform: 'uppercase', color: TITRE_SECTION}}>
                                {`${ok.length} zone${ok.length > 1 ? 's' : ''} OK`}
                            </Box>
                            <Box sx={{display: 'flex', gap: '8px', flexWrap: 'wrap'}}>
                                {ok.map((el) => (
                                    <Box key={el.libelle} component="span" sx={{px: '12px', py: '5px', borderRadius: '999px', bgcolor: '#F6F8F4', border: `1px solid ${CARTE.bordure}`, fontSize: 12, fontWeight: 600, color: TEXTE}}>{el.libelle}</Box>
                                ))}
                            </Box>
                        </Box>
                    )}
                </Box>
            )}
        </FicheBlocActe>
    );
}

function BlocGraffiti({evenement, numero, onPhotos}) {
    const toutes = photosEvenement(evenement);
    const ouvrir = (src) => onPhotos(toutes, src);
    return (
        <FicheBlocActe numero={numero} titre="Graffiti" Icone={BrushOutlinedIcon} tokens={TOKENS.graffiti}>
            {toutes.length > 0 && (
                <Box sx={grilleSx}>
                    {toutes.map((p) => <Tuile key={p.src} chemins={[p.src]} label="GRAFFITI" color={PHOTO_ETIQUETTES.GRAFFITI} onOuvrir={ouvrir}/>)}
                </Box>
            )}
        </FicheBlocActe>
    );
}

function BlocImpossible({evenement, onPhotos}) {
    const toutes = photosEvenement(evenement);
    const ouvrir = (src) => onPhotos(toutes, src);
    const motifs = evenement.motifs || [];
    return (
        <FicheBlocActe numero={1} titre="Lavage impossible" Icone={CloseIcon} tokens={TOKENS.impossible} valeur={motifs.length > 0 ? motifs.join(', ') : null}>
            <Box sx={{display: 'flex', flexDirection: 'column', gap: '14px'}}>
                <Box sx={{display: 'flex', gap: '8px', flexWrap: 'wrap'}}>
                    {(motifs.length > 0 ? motifs : ['—']).map((m) => (
                        <Box key={m} component="span" sx={{px: '12px', py: '5px', borderRadius: '999px', bgcolor: ACTE_COULEURS.probleme.fond, border: `1px solid ${ACTE_COULEURS.probleme.bordure}`, fontSize: 12, fontWeight: 700, color: ROUGE}}>{`Motif : ${m}`}</Box>
                    ))}
                </Box>
                {toutes.length > 0 && (
                    <Box sx={grilleSx}>
                        {toutes.map((p) => <Tuile key={p.src} chemins={[p.src]} label="LAVAGE IMPOSSIBLE" color={PHOTO_ETIQUETTES.IMPOSSIBLE} onOuvrir={ouvrir}/>)}
                    </Box>
                )}
            </Box>
        </FicheBlocActe>
    );
}

/** Blocs d'acte du dernier passage (R3). `blocs` : sortie de `blocsDuPassage`. Jamais de bloc « aucun ». */
export default function FicheBlocsPassage({blocs, quantite, onPhotos}) {
    let numero = 0;
    return (
        <Box sx={{display: 'flex', flexDirection: 'column', gap: '16px', minWidth: 0}}>
            {blocs.map((b) => {
                numero += 1;
                if (b.type === 'IMPOSSIBLE') return <BlocImpossible key={b.type} evenement={b.evenement} onPhotos={onPhotos}/>;
                if (b.type === 'LAVAGE') return <BlocLavage key={b.type} evenement={b.evenement} numero={numero} quantite={quantite} onPhotos={onPhotos}/>;
                if (b.type === 'MAINT_PREV') return <BlocMaintenance key={b.type} evenement={b.evenement} numero={numero} onPhotos={onPhotos}/>;
                return <BlocGraffiti key={b.type} evenement={b.evenement} numero={numero} onPhotos={onPhotos}/>;
            })}
        </Box>
    );
}
